import { useAppDispatch, useAppSelector } from "src/store/hooks";

// Type
import { AppStatus } from "src/store/context/reducer";
import { useEffect, useState } from "react";

// Ws
import { ws } from "src/services/ws";

// Actions
import { setTransferStatus, togglePaSelected } from "src/store/call/reducer";

// Components
import SidePanel from "./panel";
import { IcomDropdown, type Option } from "src/components/icom-dropdown";
import { DialerStatus } from "src/services/dialer";
import { trackEvent } from "src/services/logger";

export default function PanelTransference({ togglePanel }: { togglePanel: Function }) {
  // Redux variables
  const { availablePas, appStatus } = useAppSelector((state) => state.context);
  const { callDetails, transferPaSelected, twilioRoomName, udaQueueId } = useAppSelector((state) => state.call);

  // Dispatch
  const dispatch = useAppDispatch();
  const [state, setState] = useState<{
    selectedPa: Option | null;
    submiting: boolean;
    isPaConferenceNeeded: boolean;
    transferMessage: string;
  }>({
    selectedPa: null,
    submiting: false,
    isPaConferenceNeeded: callDetails?.callType === "video",
    transferMessage: "",
  });

  useEffect(() => {
    if (appStatus === AppStatus.IDLE) dispatch(togglePaSelected(""));
  }, [appStatus]);

  // Primary function
  const handleTransfer = async () => {
    const paQueueId = transferPaSelected;
    const callId = callDetails!.id;

    // TODO: Check saldo
    const wsEvent: {
      type: string;
      data: {
        isPaConferenceNeeded: boolean;
        destinationPaQueueId: string;
        callId: string;
        udaQueueId: string;
        transferMessage: string;
        webchatTextHistory?: string;
        twilioRoomName?: string;
        currentExternalCallNumber?: string;
      };
    } = {
      type: "PA_TRANSFER_CALL_TO",
      data: {
        isPaConferenceNeeded: state.isPaConferenceNeeded || false,
        destinationPaQueueId: paQueueId,
        callId,
        udaQueueId: udaQueueId!,
        transferMessage: state.transferMessage,
        webchatTextHistory: JSON.stringify(
          callDetails?.messageList.map((m, index) => ({
            ...m,
            username: m.name,
            message: m.text,
            userType: m.self ? "senderUser" : "destinationUser",
            sameAuthorLast: !!(callDetails?.messageList[index - 1] && callDetails?.messageList[index - 1].name === m.name),
          })),
        ),
      },
    };

    // If call
    const voipDialerInstance = callDetails?.dialerInstances?.find((d) => d.instanceName === "VOIP");

    if (voipDialerInstance && voipDialerInstance.getStatus() === DialerStatus.ON_CALL) {
      wsEvent.data.currentExternalCallNumber = voipDialerInstance.getInput();
    }

    // If a twilioroom exists
    if (twilioRoomName) wsEvent.data["twilioRoomName"] = twilioRoomName;

    // Update call transferece status
    dispatch(
      setTransferStatus({
        inTransference: true,
        isTransferenceOrigin: true,
        isConferenceNeeded: state.isPaConferenceNeeded,
      }),
    );

    // Send websockets message
    ws?.sendMessage(wsEvent);
    trackEvent("call_transfer_initiated", {
      call_type: callDetails?.callType,
      requires_conference: state.isPaConferenceNeeded,
    });
  };

  // Map status to Option pattern
  const listOptions = availablePas
    .filter((p) => {
      if (appStatus === AppStatus.IN_CALL && callDetails && callDetails?.aderenteResponsavelSlug === "abr") {
        return p.hasMorgaCertificate && ["Available", "Waiting Reverse Transfer"].includes(p.status);
      }

      return ["Available", "Waiting Reverse Transfer"].includes(p.status);
    })
    .map((s) => ({
      id: s.paQueueId,
      label: `${s.paName} - ${s.ramal}`,
      value: s.paQueueId,
    }));

  return (
    <SidePanel panelName="Transferir chamada" toggleAction={togglePanel}>
      <div className="flex flex-col justify-between h-full">
        <div className="transfer-top-group">
          <div className="form-group mb-3">
            <label htmlFor="paselect" className="font-bold mb-1 block">
              Pra quem você quer transferir?
            </label>
            <IcomDropdown
              id="paselect"
              selectedOption={transferPaSelected}
              onChange={(option: Option) => {
                dispatch(togglePaSelected(option.id));
              }}
              listOptions={listOptions}
            />
          </div>

          {callDetails?.callType === "video" && (
            <div className="form-group mb-3">
              <label htmlFor="precall" className="font-bold mb-1 block">
                Necessita de pré-conferencia?
              </label>
              <IcomDropdown
                id="precall"
                selectedOption={state.isPaConferenceNeeded}
                onChange={(option: Option) =>
                  setState((s) => ({
                    ...s,
                    isPaConferenceNeeded: option.value,
                  }))
                }
                listOptions={[
                  {
                    id: "true",
                    label: "Sim",
                    value: true,
                  },
                  {
                    id: "false",
                    label: "Não",
                    value: false,
                  },
                ]}
              />
            </div>
          )}

          <div className="form-group mb-3">
            <label htmlFor="observacoes" className="font-bold mb-1 block">
              Precisa informar algo pro novo interprete?
            </label>
            <textarea
              id="observacoes"
              name="observacoes"
              onChange={(e) => {
                const { value } = e.target;
                setState((s) => ({ ...s, transferMessage: value }));
              }}
              placeholder="Adicione aqui observações"
              className="w-full border-neutral-gray-200 rounded-lg p-3 border-solid border"
              rows={3}
            ></textarea>
          </div>
        </div>

        <button
          disabled={!transferPaSelected || callDetails?.transferenceStatus.inTransference}
          //     disabled={appStatus !== AppStatus.IN_CALL || !transferPaSelected}
          onClick={handleTransfer}
          className="h-12 bg-primary-blue-400 text-base font-bold text-white flex items-center justify-center rounded-lg hover:bg-primary-blue-500 disabled:bg-gray-300 disabled:text-gray-400"
          type="button"
        >
          {callDetails?.transferenceStatus.inTransference ? "Transferindo..." : "Transferir"}
        </button>
      </div>
    </SidePanel>
  );
}
